/** STOCKS — the valuation master behind a fund's look-through holdings. */
import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import {
  STOCKS_BY_SYMBOLS, STOCK_NAMES_BY_SYMBOLS, SEARCH_MUTUAL_FUNDS, GET_FUND,
  GET_STOCKS, GET_STOCK_DETAILS, STOCK_RESEARCH_REPORTS, SEARCH_SECURITIES,
} from '../queries';
import { EMPTY } from './_shared';
import { weightedHarmonic, weightedMean } from '../../lib/portfolio';
import { VALUATION_METRICS } from '../../lib/valuation';

/**
 * What a fund's holdings are valued at, and what that makes the fund.
 *
 * Holdings arrive from the scheme master carrying NSE symbols, which key
 * straight into the stock master. Cash, repo and government securities carry
 * an internal id and resolve to nothing — they are counted as unpriced rather
 * than dropped silently, because a fund holding 8% cash is a fund whose
 * multiple speaks for 92% of it.
 *
 * Holdings arrive already normalised — `weight` a fraction, `weightPct` a
 * percentage — from either `fundHoldings` or `usePortfolio`, so the roll-up
 * can divide by covered weight without guessing the units.
 */
export function useHoldingValuations(holdings, { skip = false } = {}) {
  const symbols = useMemo(
    () => holdings.map((h) => h.holdingSecuritySymbol).filter(Boolean),
    [holdings],
  );

  const { data, loading, error } = useQuery(STOCKS_BY_SYMBOLS, {
    variables: { securitySymbols: symbols },
    skip: skip || symbols.length === 0,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  return useMemo(() => {
    const master = new Map(
      (data?.getStocksBySymbols ?? EMPTY).map((s) => [s.securitySymbol, s]),
    );

    const rows = holdings.map((h) => {
      const stock = master.get(h.holdingSecuritySymbol) ?? null;
      const f = stock?.fundamentals ?? null;
      return {
        symbol: h.holdingSecuritySymbol,
        name: stock?.name || h.name || h.holdingSecuritySymbol,
        sector: stock?.sector || h.sector || null,
        weight: Number(h.weight),
        weightPct: Number(h.weightPct),
        /* Carried through so the weight cell can pick the same history the
           look-through table does: a position the factor run reached has a
           daily weight series, one it did not has only the disclosures. */
        scored: Boolean(h.contribution),
        priced: Boolean(f),
        marketCap: f?.marketCap ?? null,
        peRatio: f?.peRatio ?? null,
        forwardPe: f?.forwardPe ?? null,
        pegRatio: f?.pegRatio ?? null,
        priceBookMrq: f?.priceBookMrq ?? null,
        priceSalesTtm: f?.priceSalesTtm ?? null,
        enterpriseValueEbitda: f?.enterpriseValueEbitda ?? null,
        dividendYield: f?.dividendYield ?? null,
        returnOnEquityTtm: f?.returnOnEquityTtm ?? null,
        pePercentile: f?.pePercentile ?? null,
        peSignal: f?.peSignal ?? null,
        asOfDate: f?.asOfDate ?? null,
      };
    }).sort((a, b) => b.weightPct - a.weightPct);

    const priced = rows.filter((r) => r.priced);
    /* The fund's own multiple for each ratio, averaged the way that ratio
       legitimately averages — see VALUATION_METRICS. */
    const rollup = VALUATION_METRICS.map(({ key, label, pick, harmonic }) => {
      const { value, covered } = (harmonic ? weightedHarmonic : weightedMean)(priced, pick);
      return { key, label, value, covered };
    });

    return {
      loading,
      error,
      rows,
      rollup,
      priced: priced.length,
      universe: rows.length,
      /* The weight the fund's own multiples actually speak for. */
      pricedWeight: priced.reduce((t, r) => t + (Number.isFinite(r.weight) ? r.weight : 0), 0),
      asOfDate: priced.find((r) => r.asOfDate)?.asOfDate ?? null,
    };
  }, [data, holdings, loading, error]);
}

/**
 * A benchmark's name reduced to something two records can be matched on.
 * "NIFTY 500 Total Return Index" and "Nifty-500 TRI" are the same basket —
 * the return convention differs, the composition does not.
 */
const indexKey = (name) =>
  String(name || '')
    .toLowerCase()
    .replace(/\b(tri|total return|price return|index)\b/g, ' ')
    .replace(/[^a-z0-9]+/g, '');

/**
 * The benchmark name as a search term. The master is searched by FUND name and
 * no fund is called "NIFTY 500 Total Return Index", so the return convention
 * has to come off first: "NIFTY 500" then finds every tracker of it. "Index"
 * is kept, because it is in the trackers' own names and narrows a broad index
 * to its plain trackers rather than its Value 50 and Equal Weight cousins.
 */
const searchTermFor = (name) =>
  `${String(name || '').replace(/\b(tri|total return|price return)\b/gi, ' ')
    .replace(/\s+/g, ' ').trim()} Index`.replace(/(\bIndex\b)(\s+\1)+/gi, '$1').trim();

/**
 * The same valuation, run on the fund's benchmark.
 *
 * An index fund's disclosed holdings ARE the index, so the benchmark is valued
 * through a tracker rather than through the benchmark register: the register
 * is owned per user and answers empty for anyone who has not created rows in
 * it, while the scheme master is public and carries the composition already.
 * The SBI Nifty 500 tracker discloses 502 names summing to 100%, of which 500
 * price — a better read of the index than the register could give.
 *
 * A tracker is chosen by the benchmark it names, not by its own name, so a
 * fund benchmarked to the NIFTY 500 finds any NIFTY 500 tracker. Its holdings
 * lag the index by the disclosure cycle and its weights carry a little cash,
 * which is why this is described on screen as the tracker it came from.
 */
export function useBenchmarkValuation(benchmarkName, { skip = false } = {}) {
  const wanted = indexKey(benchmarkName);

  /* Trackers are found by searching the master for the index's own name. */
  const search = useQuery(SEARCH_MUTUAL_FUNDS, {
    variables: { searchQuery: searchTermFor(benchmarkName), limit: 50, offset: 0 },
    skip: skip || !wanted,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  const tracker = useMemo(() => {
    const funds = search.data?.getAllMutualFunds?.funds ?? EMPTY;
    const trackers = funds.filter((f) => f.isIndexFund && indexKey(f.benchmarkName) === wanted);
    /* Any tracker of the same index will do; the first is as good as another. */
    return trackers[0] ?? null;
  }, [search.data, wanted]);

  const { data, loading, error } = useQuery(GET_FUND, {
    variables: { symbol: tracker?.securitySymbol },
    skip: skip || !tracker?.securitySymbol,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  const constituents = useMemo(() => {
    const rows = data?.getMetaMutualFund?.holdings ?? EMPTY;
    return rows.map((h) => {
      const pct = Number(h.weight) || 0;
      return {
        holdingSecuritySymbol: h.securitySymbol,
        name: h.name || h.securitySymbol,
        weight: pct / 100,
        weightPct: pct,
      };
    });
  }, [data]);

  const valued = useHoldingValuations(constituents, { skip: skip || !constituents.length });

  return useMemo(() => ({
    ...valued,
    loading: search.loading || loading || valued.loading,
    error: search.error ?? error ?? valued.error,
    name: benchmarkName ?? null,
    /* What the figures were actually read off, named on screen so the number
       is not passed off as the index's own published multiple. */
    via: tracker?.fundName ?? null,
    matched: Boolean(tracker),
    hasHoldings: constituents.length > 0,
  }), [valued, search.loading, search.error, loading, error, benchmarkName, tracker, constituents.length]);
}

/* ── the stock master as a screen ───────────────────────────── */

/**
 * The browse list, paged.
 *
 * `getStocks` takes no search argument — its filters are country, exchange and
 * sector — so a symbol lookup is a different query against a different
 * resolver. This hook runs whichever the screen is currently asking for and
 * presents one shape either way, so the table does not branch.
 *
 * The list resolver answers with a page and no total, so the pager can only say
 * whether a next page exists: it asks for one row more than it shows and drops
 * it. `network-only`, because the master is edited by the sync jobs and a
 * cached page ages badly against a price column.
 */
export function useStockUniverse({ symbol, sector, exchange, page = 0, perPage = 20 } = {}) {
  const lookup = String(symbol || '').trim().toUpperCase();

  const list = useQuery(GET_STOCKS, {
    variables: {
      skip: page * perPage,
      limit: perPage + 1,
      sector: sector || null,
      exchange: exchange || null,
    },
    skip: Boolean(lookup),
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const one = useQuery(GET_STOCK_DETAILS, {
    variables: { securitySymbol: lookup },
    skip: !lookup,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  return useMemo(() => {
    if (lookup) {
      const hit = one.data?.getStockDetails ?? null;
      return {
        loading: one.loading,
        error: one.error,
        rows: hit ? [hit] : EMPTY,
        page: 0,
        hasNext: false,
        lookup,
      };
    }
    const rows = list.data?.getStocks ?? EMPTY;
    return {
      loading: list.loading,
      error: list.error,
      rows: rows.slice(0, perPage),
      page,
      hasNext: rows.length > perPage,
      lookup: null,
    };
  }, [lookup, one.data, one.loading, one.error, list.data, list.loading, list.error, page, perPage]);
}

/**
 * One stock's whole record, with its research reports beside it.
 *
 * The reports come in two calls against the same resolver: the current one per
 * archetype, and the full history including superseded generations. They are
 * separate because the screen shows the current report as the headline and the
 * rest as an audit trail, and a single call cannot be both.
 */
export function useStockDetail(symbol) {
  const off = !symbol;

  const detail = useQuery(GET_STOCK_DETAILS, {
    variables: { securitySymbol: symbol },
    skip: off,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const current = useQuery(STOCK_RESEARCH_REPORTS, {
    variables: { securitySymbol: symbol, includeHistory: false },
    skip: off,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const history = useQuery(STOCK_RESEARCH_REPORTS, {
    variables: { securitySymbol: symbol, includeHistory: true },
    skip: off,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  return useMemo(() => {
    const stock = detail.data?.getStockDetails ?? null;
    const reports = current.data?.getAllStockResearchReports ?? EMPTY;
    const all = history.data?.getAllStockResearchReports ?? EMPTY;
    return {
      loading: detail.loading,
      error: detail.error,
      stock,
      fundamentals: stock?.fundamentals ?? null,
      snapshot: stock?.snapshot ?? null,
      /* Newest first — the master returns filing order, not report order. */
      financials: [...(stock?.financialStatements ?? EMPTY)].sort(
        (a, b) => String(b.periodEndDate || '').localeCompare(String(a.periodEndDate || '')),
      ),
      earnings: [...(stock?.earnings ?? EMPTY)].sort(
        (a, b) => String(b.reportDate || '').localeCompare(String(a.reportDate || '')),
      ),
      estimates: stock?.estimates ?? EMPTY,
      peers: stock?.peerComparison ?? null,
      similar: stock?.similarStocks ?? EMPTY,
      diversifying: stock?.diversifyingStocks ?? EMPTY,
      factors: stock?.factorExposures ?? null,
      report: reports[0] ?? null,
      reports,
      /* Superseded generations only — the current one is already the headline. */
      history: all.filter((r) => !reports.some((c) => c.id === r.id)),
      reportsLoading: current.loading || history.loading,
      refetchReports: () => Promise.all([current.refetch(), history.refetch()]),
    };
  }, [detail.data, detail.loading, detail.error, current, history]);
}

/** The whole research catalogue — every stock a report has been written for. */
export function useStockReports({ limit = 50, offset = 0 } = {}) {
  const { data, loading, error, refetch } = useQuery(STOCK_RESEARCH_REPORTS, {
    variables: { limit, offset, includeHistory: false },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  return {
    loading,
    error,
    refetch,
    reports: data?.getAllStockResearchReports ?? EMPTY,
  };
}

/**
 * Each holding with the stock master's own risk figures — volatility, beta,
 * Sharpe and drawdown — for reading what a fund's realized characteristics are
 * made of. One query for the whole book, shared with the valuation read.
 *
 * `contribution` is the holding's weight times its own annualised volatility: a
 * weighted decomposition, NOT a covariance one. The parts sum to more than the
 * fund's own volatility because its stocks do not move together, which is the
 * diversification the fund is getting; the panel says so rather than implying
 * an exact split.
 */
export function useHoldingRiskStats(holdings, { skip = false } = {}) {
  const symbols = useMemo(
    () => (holdings ?? EMPTY).map((h) => h.holdingSecuritySymbol).filter(Boolean),
    [holdings],
  );
  const { data, loading, error } = useQuery(STOCKS_BY_SYMBOLS, {
    variables: { securitySymbols: symbols },
    skip: skip || symbols.length === 0,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  return useMemo(() => {
    const master = new Map((data?.getStocksBySymbols ?? EMPTY).map((s) => [s.securitySymbol, s]));
    const num = (v) => (v == null || Number.isNaN(Number(v)) ? null : Number(v));
    const rows = (holdings ?? EMPTY).map((h) => {
      const f = master.get(h.holdingSecuritySymbol)?.fundamentals ?? null;
      const weight = Number(h.weight) || 0;
      const volatility = num(f?.volatilityAnnual);
      return {
        symbol: h.holdingSecuritySymbol,
        name: master.get(h.holdingSecuritySymbol)?.name || h.name || h.holdingSecuritySymbol,
        weight,
        weightPct: Number(h.weightPct) || weight * 100,
        volatility,
        beta: num(f?.beta),
        sharpe: num(f?.sharpeRatio),
        maxDrawdown: num(f?.maxDrawdown),
        priced: volatility != null,
        contribution: volatility == null ? null : weight * volatility,
      };
    });
    const priced = rows.filter((r) => r.priced);
    return {
      loading,
      error,
      rows: [...rows].sort((a, b) => (b.contribution ?? -1) - (a.contribution ?? -1)),
      priced: priced.length,
      universe: rows.length,
      /** Weight covered by stocks the master prices — the rest cannot contribute a figure. */
      coveredWeight: priced.reduce((t, r) => t + r.weight, 0),
      totalContribution: priced.reduce((t, r) => t + r.contribution, 0),
      weightedBeta: priced.reduce((t, r) => t + (r.beta == null ? 0 : r.weight * r.beta), 0),
    };
  }, [holdings, data, loading, error]);
}

/**
 * Name search over the security master, for a typeahead.
 *
 * `kind` is the resolver's own lower-case entity type — 'stock' or 'mf' — or
 * null for both. Skipped below two characters: one letter matches thousands of
 * names and the answer is no use to anybody.
 *
 * `fuzzy` says the master held no outright match and these are guesses at a
 * misspelling, so the screen can label them rather than showing a list of
 * unrelated companies as though they were hits.
 */
export function useSecuritySearch(query, { kind = null, limit = 8, etf = null } = {}) {
  const term = (query ?? '').trim();
  const { data, loading, error } = useQuery(SEARCH_SECURITIES, {
    /* `etf` true narrows to exchange-traded funds — the benchmark picker's universe. */
    variables: { query: term, limit, entityType: kind, isEtf: etf },
    skip: term.length < 2,
    fetchPolicy: 'cache-first',
  });
  const results = data?.searchSecurities ?? EMPTY;
  return {
    loading,
    error,
    results,
    /* The resolver scores an outright match 1.0 and a fuzzy one well below it. */
    fuzzy: results.length > 0 && results.every((r) => (r.similarity ?? 1) < 0.9),
  };
}

/**
 * Stock symbols to names, by the names-only document — for naming a
 * look-through, whose routes end in far more stocks than `useSecurityNames`
 * was written to fetch whole records for. Same shape as that hook's answer.
 */
export function useStockNames(symbols, { skip = false } = {}) {
  const key = (symbols ?? EMPTY).filter(Boolean).join('|');
  const { data, loading } = useQuery(STOCK_NAMES_BY_SYMBOLS, {
    variables: { securitySymbols: key ? key.split('|') : EMPTY },
    skip: skip || !key,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  return useMemo(() => {
    const bySymbol = new Map(
      (data?.getStocksBySymbols ?? EMPTY).filter((r) => r.name).map((r) => [r.securitySymbol, r.name]),
    );
    return { loading, bySymbol, nameOf: (symbol) => bySymbol.get(symbol) || symbol };
  }, [data, loading]);
}

/**
 * Symbols to the names people actually read.
 *
 * The style-factor endpoints answer in symbols only — `holdingSecuritySymbol`
 * and nothing else — so a grid built straight off them is a column of tickers.
 * `getStocksBySymbols` is the equity master keyed the same way, and every style
 * holding is an equity by construction, so one call names the lot.
 *
 * Returns a lookup that falls back to the symbol, so an unresolved name never
 * blanks a row: a ticker is a worse label than a name and a better one than
 * nothing.
 */
export function useSecurityNames(symbols, { skip = false } = {}) {
  const key = (symbols ?? EMPTY).filter(Boolean).join('|');
  const { data, loading } = useQuery(STOCKS_BY_SYMBOLS, {
    variables: { securitySymbols: key ? key.split('|') : EMPTY },
    skip: skip || !key,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  return useMemo(() => {
    const bySymbol = new Map(
      (data?.getStocksBySymbols ?? EMPTY).map((r) => [r.securitySymbol, r.name]),
    );
    return {
      loading,
      bySymbol,
      nameOf: (symbol) => bySymbol.get(symbol) || symbol,
    };
  }, [data, loading]);
}
